import React, { useEffect, useState } from 'react';

/**
 * v3.18-1359 CLOCK-001 — Live clock designed to sit in the toolbar
 * whitespace between the search input and the date range control on
 * the Clients page.
 *
 * • Updates every second via setInterval.
 * • Timezone precedence: user.timezone → user.time_zone → browser tz.
 * • Renders inline (no fixed width) so the parent flex container can
 *   size the surrounding whitespace naturally. Uses `whitespace-nowrap`
 *   so the 12h / 24h / tz-name pieces never wrap onto two lines.
 * • Hidden on the smallest breakpoints so the search + date controls
 *   keep their real estate on mobile.
 */
export default function ToolbarClock({ user, className = '' }) {
  const configuredTz =
    user?.timezone ||
    user?.time_zone ||
    user?.settings?.timezone ||
    null;
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tz = configuredTz || browserTz || 'UTC';

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const fmt12 = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: tz,
  });
  const fmt24 = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: tz,
  });

  return (
    <div
      className={`hidden md:flex items-center gap-3 text-sm font-mono text-slate-600 whitespace-nowrap select-none ${className}`}
      title={`Clock — ${tz}${configuredTz ? '' : ' (browser default)'}`}
      data-testid="toolbar-live-clock"
    >
      <span className="tabular-nums text-slate-800" data-testid="clock-12h">
        {fmt12.format(now)}
      </span>
      <span className="text-slate-300">|</span>
      <span className="tabular-nums text-slate-800" data-testid="clock-24h">
        {fmt24.format(now)}
      </span>
      <span className="text-slate-300">|</span>
      <span className="text-slate-500" data-testid="clock-tz">
        {tz}
      </span>
    </div>
  );
}
